import { CATEGORY_KEYS, CategoryKey, categoryKeysForChain } from 'constants/tokenLists/categories'
import { ComponentType } from 'react'
import { Award, BarChart2, DollarSign, Droplet, Icon, Layers, List, MoreHorizontal } from 'react-feather'
import styled from 'styled-components'

export const ALL_TOKENS_FILTER = 'All'

/** The token category the selector shows, or every token. */
export type TokenFilter = typeof ALL_TOKENS_FILTER | CategoryKey

const CATEGORY_ICONS: Record<CategoryKey, Icon> = {
  [CATEGORY_KEYS.MAIN]: Layers,
  [CATEGORY_KEYS.LP_TOKENS]: Droplet,
  [CATEGORY_KEYS.RWA]: BarChart2,
  [CATEGORY_KEYS.USD]: DollarSign,
  [CATEGORY_KEYS.MAJORS]: Award,
  [CATEGORY_KEYS.OTHERS]: MoreHorizontal,
}

/** The tabs of a chain: every token first, then its categories. */
export function tokenFiltersForChain(chainId: number | undefined): readonly TokenFilter[] {
  return [ALL_TOKENS_FILTER, ...categoryKeysForChain(chainId)]
}

const iconOf = (filter: TokenFilter): Icon => (filter === ALL_TOKENS_FILTER ? List : CATEGORY_ICONS[filter])

const TabRow = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
  overflow-x: auto;
  border-bottom: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }
`

const TabButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1 0 auto;
  padding: 8px 10px;
  margin-bottom: -1px;
  background: none;
  border: none;
  border-bottom: 2px solid ${({ theme, $active }) => ($active ? theme.accentAction : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.accentAction : theme.textSecondary)};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease;

  :hover {
    color: ${({ theme, $active }) => ($active ? theme.accentAction : theme.textPrimary)};
  }

  :focus-visible {
    outline: 1px solid ${({ theme }) => theme.accentAction};
  }
`

export default function TokenTabs({
  filters,
  selected,
  onSelect,
}: {
  filters: readonly TokenFilter[]
  selected: TokenFilter
  onSelect: (filter: TokenFilter) => void
}) {
  return (
    <TabRow role="tablist">
      {filters.map((key) => {
        const TabIcon = iconOf(key) as ComponentType<{ size: number }>
        const isActive = selected === key
        return (
          <TabButton
            key={key}
            type="button"
            role="tab"
            aria-selected={isActive}
            $active={isActive}
            onClick={() => onSelect(key)}
          >
            <TabIcon size={14} />
            {key}
          </TabButton>
        )
      })}
    </TabRow>
  )
}
